/**
 * This self-invoking function handles hover effects for the buttons in the 'Collection list hover' section.
 * It listens for mouseover and mouseout events to add or remove a CSS class.
 */
(function() {
  /**
   * Update the active button by adding or removing the specified class name.
   * @param {Array} array - Array of button elements.
   * @param {HTMLElement} target - The target button element that should get the class.
   * @param {String} className - The class name to be added or removed.
   */
  function updateActiveButton(array, target, className) {
    array.forEach(item => {
      if (item === target) {
        item.classList.add(className);
      } else {
        item.classList.remove(className);
      }
    });
  }
  
  /**
   * Attach mouseenter event handlers to each button in the given array.
   * @param {Array} array - Array of button elements.
   * @param {String} className - The class name to be added when hovered.
   */
  function attachHoverEventHandlers(array, className) {
    array.forEach((item) => {
      item.addEventListener('mouseenter', (e) => {
        updateActiveButton(array, e.target, className);
      });
    });
  }

  /**
   * Initialize hover functionality on buttons.
   */
  function initHoverButtons() {
    // Get an array of buttons from the DOM
    const buttons = Array.from(document.querySelectorAll('.mx-hover-collection--button'));
    // Class to be added on hover
    const className = 'mx-is-active';
  
    // If no buttons are found, exit the function
    if (!buttons.length) return;
  
    // Attach hover event handlers
    attachHoverEventHandlers(buttons, className);
  }
  
  // Call the initialization function
  initHoverButtons();

})();

/**
 * This self-invoking function handles hover effects for SVG button backgrounds sitewide.
 * It listens for mouseover and mouseout events to add or remove a CSS class.
 */
(function() {
  // Get an array of button containers from the DOM.
  const buttons = Array.from(document.querySelectorAll('.mx-button--container'));

  // If no button containers are found, exit the function.
  if (buttons.length < 1) return;

  // Loop through each button container.
  buttons.forEach(button => {
    // Add a 'mouseover' event listener to each button.
    button.addEventListener('mouseover', () => {
      // Add a 'hovered' class to the button when mouseover event is triggered.
      button.classList.add('mx-button--container__hovered');
    });

    // Add a 'mouseout' event listener to each button.
    button.addEventListener('mouseout', () => {
      // Remove the 'hovered' class when the mouseout event is triggered.
      button.classList.remove('mx-button--container__hovered');
    });
  });
})();


/**
 * Creates a wrapper element for form fields and returns it.
 *
 * @returns {HTMLElement} The wrapper element with a 'form-field--wrapper' class.
 */
function createFormFieldWrapper() {
  const wrapperElement = document.createElement('div');
  wrapperElement.classList.add('form-field--wrapper');
  return wrapperElement;
}

/**
 * Creates a label element for a given input field and returns it.
 *
 * @param {HTMLInputElement} input - The input element for which the label is created.
 * @returns {HTMLElement} The label element associated with the input field.
 */
function createLabelsForInputFields(input) {
  const labelElement = document.createElement('label');
  labelElement.setAttribute('for', input.id);
  labelElement.textContent = input.placeholder;
  return labelElement;
}

/**
 * Wraps input fields with a wrapper element and adds associated labels.
 *
 * @param {string} wrapper - The ID of the element that contains the form.
 * @param {string} form - The selector for the form element within the wrapper.
 */
function wrapInputFieldsAndAddLabels(wrapper, form) {
  const formWrapper = document.getElementById(wrapper);
  // Check if the wrapper element exists
  if (!formWrapper) return;

  const formElement = formWrapper.querySelector(form);
  // Check if the form element exists within the wrapper
  if (!formElement) return;

  const inputElements = Array.from(formElement.querySelectorAll('input[type="email"], input[type="password"]'));

  inputElements.forEach(input => {
    const parentElement = input.parentElement;
    const labelElement = createLabelsForInputFields(input);
    const wrapperElement = createFormFieldWrapper();

    parentElement.appendChild(wrapperElement);
    wrapperElement.appendChild(labelElement);
    wrapperElement.appendChild(input);
  });
}


/**
 * Adds 'focus' and 'blur' event listeners to an input element to toggle relevant classes.
 *
 * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} input - The input element to which events are added.
 * @param {HTMLElement} parentElement - The parent element of the input.
 */
function addInputEventListener(input, parentElement) {
  input.addEventListener('focus', () => {
    parentElement.classList.remove('has-value');
    parentElement.classList.add('has-focus');
  });

  input.addEventListener('blur', () => {
    parentElement.classList.remove('has-focus');
    if (input.value) {
      if (input.tagName !== 'SELECT') {
        parentElement.classList.add('has-value');
      } else if (input.value !== '- Pick an option -') {
        parentElement.classList.add('has-value');
      } else {
        parentElement.classList.remove('has-value');
      }
    } else {
      parentElement.classList.remove('has-value');
    }
  });
}

/**
 * Initializes label animations for form fields.
 *
 * @param {string} form - The selector for the form element.
 * @param {boolean} isNativeForm - Flag to indicate if the form uses native form elements.
 */
function initFormLabelAnimation(form, isNativeForm = false) {
  const formElement = document.querySelector(form);
  // Check if the form element exists
  if (!formElement) return;

  const inputElements = isNativeForm ?
    Array.from(formElement.elements) :
    Array.from(formElement.querySelectorAll('input, textarea, select'));

  inputElements.forEach(input => {
    const parentElement = input.parentElement;
    const labelElement = parentElement.querySelector('label');
    
    if (labelElement) {
      const inputType = input.type;
      labelElement.setAttribute('data-input-type', inputType);
    }

    addInputEventListener(input, parentElement);
  });
}


/**
 * Returns the full dimensions of an element based on a CSS selector.
 *
 * @param {string} selector - The CSS selector to identify the element.
 * @returns {DOMRect|null} A DOMRect object containing the size of an element and its position relative to the viewport, or null if the element doesn't exist.
 */
function getFullDimensions(selector) {
  const element = document.querySelector(selector);
  return element ? element.getBoundingClientRect() : null;
}

/**
 * Converts an object into an array of objects with specified key and value names.
 *
 * @param {Object} obj - The object to convert.
 * @param {string} keyName - The property name for keys in the resulting objects.
 * @param {string} valueName - The property name for values in the resulting objects.
 * @returns {Array} An array of objects, each having properties as specified by keyName and valueName.
 */
function mapObjectToArray(obj, keyName, valueName) {
  return Object.keys(obj).map(key => ({
    [keyName]: `--${key}`,
    [valueName]: obj[key]
  }));
}

/**
 * Stores dimensions of multiple elements based on an array of element data.
 *
 * @param {Array} elements - An array of objects containing name and selector for each element.
 * @param {string} dimension - The dimension (e.g., 'width', 'height') to store.
 * @returns {Object} An object where each key is the name of an element and the value is the requested dimension.
 */
function storeDimensions(elements, dimension) {
  return elements.reduce((acc, el) => {
    acc[el.name] = getFullDimensions(el.selector)?.[dimension];
    return acc;
  }, {});
}

/**
 * Sets custom CSS properties (variables) on a root HTML element based on dimensions.
 *
 * @param {Array} elements - An array of objects containing name and selector for each element.
 * @param {string} dimension - The dimension (e.g., 'width', 'height') to set.
 * @param {HTMLElement} rootElement - The root element where the custom properties will be set.
 * @returns {Object} An object containing the set dimensions.
 */
function setRootVars(elements, dimension, rootElement) {
  const dimensions = storeDimensions(elements, dimension);
  for (const [key, value] of Object.entries(dimensions)) {
    rootElement.style.setProperty(key, `${value}px`);
  }
  return dimensions;
}

// Configuration object for ResizeObserver
const resizeConfig = {
  observedElement: document.querySelector('[data-header-height]'),
  targetElement: document.documentElement,
  dimension: 'height',
  elementData: {
    'menu-height': '[data-header-height]',
    'announcement-height': '[data-section-type*="announcement"] [data-bar-top]'
  }
};

// Create and observe with ResizeObserver
const resizeObserver = createResizeObserver(resizeConfig);
resizeObserver.observe(resizeConfig.observedElement);

/**
 * Creates a ResizeObserver to observe changes to an element's dimensions.
 *
 * @param {Object} config - The configuration object.
 * @param {HTMLElement} config.observedElement - The DOM element to observe.
 * @param {HTMLElement} config.targetElement - The target element to apply changes to.
 * @param {string} config.dimension - The dimension (e.g., 'height', 'width') to observe.
 * @param {Object} config.elementData - An object containing data to be mapped to an array.
 * @returns {ResizeObserver|null} - Returns a new ResizeObserver instance or null if observedElement is not defined.
 */
function createResizeObserver(config) {
  const { observedElement, targetElement, dimension, elementData } = config;

  // Exit if observedElement is not defined
  if (!observedElement) return null;

  const initialHeight = config.observedElement.offsetHeight;

  return new ResizeObserver((entries, observer) => {
    for (const entry of entries) {
      const { height } = entry.contentRect;

      // Disconnect the observer if the height changes
      if (height !== initialHeight) {
        observer.disconnect();
        onElementResizeObserved(targetElement, dimension, elementData);
      }
    }
  });
}

/**
 * Called when an observed element is resized. It sets variables on the target element.
 *
 * @param {HTMLElement} targetElement - The target DOM element to apply changes to.
 * @param {string} dimension - The dimension (e.g., 'height', 'width') to set.
 * @param {Object} data - An object containing data to be mapped to an array.
 */
function onElementResizeObserved(targetElement, dimension, data) {
  const elements = mapObjectToArray(data, 'name', 'selector');
  setRootVars(elements, dimension, targetElement);
}

const mutationConfig = {
  targetElement: document.querySelector('.header__wrapper'),
  mutationType: 'attributes',
  mutationsToWatch: {
    class: 'meganav--visible'
  },
}

function createMutationObserver(config, callback) {
  const { targetElement, mutationType, mutationsToWatch } = config
  const mutationKeys = Object.keys(mutationsToWatch)

  return observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if ( mutation.type === mutationType && mutationKeys.includes(mutation.attributeName) ) {
        const target = mutation.target

        const data = [
          target,
          mutationsToWatch
        ]
        
        callback(data)
      }
    })
  })
}

function classMutationObserved(data) {
  const [target, mutationsToWatch] = data
  const observedClassName = mutationsToWatch.class
  const targetClassList = Array.from(target.classList)

  if ( targetClassList.includes(observedClassName) ) {
    target.setAttribute('data-change-cursor', '')
  } else {
    target.removeAttribute('data-change-cursor')
  }
}

const classListObserver = createMutationObserver(mutationConfig, classMutationObserved)

classListObserver.observe(mutationConfig.targetElement, {
  attributes: true,
  attributeFilter: Object.keys(mutationConfig.mutationsToWatch)
})

const OFFSET_LEFT = 25
const OFFSET_TOP = 20
const DESKTOP_BREAKPOINT = 750
const WINWIDTH = window.innerWidth
const BODY = document.body

let shouldChangeState = false
let isHovering = false

const formElementSelectors = [
  'input',
  'textarea',
  'select'
]

const linkElementSelectors = [
  'a',
  'button',
  'input[type="submit"]',
  '.tab-link',
  'header-search-popdown',
  '[data-collapsible-trigger]',
  '.flickity-button'
]

// const { cursorDarkNormal, cursorDarkHover, cursorLightNormal, cursorLightHover } = window.cursorAssets

const cursorSrc = {
  darkNormal: 'https://cdn.shopify.com/s/files/1/0753/4952/4755/files/cursor-dark--normal.svg?v=1694721267',
  darkHover: 'https://cdn.shopify.com/s/files/1/0753/4952/4755/files/cursor-dark--hover.png?v=1694743878',
  lightNormal: 'https://cdn.shopify.com/s/files/1/0753/4952/4755/files/cursor-light--normal.svg?v=1694721267',
  lightHover: 'https://cdn.shopify.com/s/files/1/0753/4952/4755/files/cursor-light--hover.svg?v=1694721266'
}

function isMobile() {
  const userAgent = window.navigator.userAgent.toLowerCase()

  return (
    userAgent.indexOf('mobi') >= 0 ||
    userAgent.indexOf('android') >= 0 ||
    document.body.clientWidth < DESKTOP_BREAKPOINT
  )
}

function getMousePosition(e) {
  return {
    x: e.clientX,
    y: e.clientY
  }
}

function updateCursorPosition(cursor, coordinates) {
  const effectiveX = coordinates.x - OFFSET_LEFT
  const effectiveY = coordinates.y - OFFSET_TOP
  // const effectiveX = (coordinates.x + window.scrollX) - OFFSET_LEFT
  // const effectiveY = (coordinates.y + window.scrollY) - OFFSET_TOP

  cursor.style.left = `${effectiveX}px`
  cursor.style.top = `${effectiveY}px`
}

function createHTMLElement(tag, attributes) {
  const element = document.createElement(tag)

  Object.keys(attributes).forEach(key => {
    element.setAttribute(key, attributes[key])
  })

  return element
}

function returnDOMElements(selectors) {
  return selectors.reduce((acc, curr) => {
    const elements = document.querySelectorAll(curr)
    if ( elements ) return [...acc, ...Array.from(elements)]
    return acc
  }, [])
}

function setCursorImage(cursorElement, type) {
  if ( shouldChangeState ) {
    cursorElement.src = type === 'hover' ? cursorSrc.lightHover : cursorSrc.lightNormal
  } else {
    cursorElement.src = type === 'hover' ? cursorSrc.darkHover : cursorSrc.darkNormal
  }
}

function cursorShouldChange(cursorElement, coordinates) {
  const { x, y } = coordinates

  const currentElement = document.elementFromPoint(x, y)
  
  if ( currentElement === null ) return

  const targetElement = currentElement.closest('[data-change-cursor]')

  shouldChangeState = targetElement !== null

  if ( !isHovering ) {
    setCursorImage(cursorElement, 'normal')
  }
}

function handleCursorChange(cursorElement, hoverElements) {
  if ( hoverElements.length < 1 ) return

  hoverElements.forEach(element => {
    element.addEventListener('mouseover', () => {
      isHovering = true
      setCursorImage(cursorElement, 'hover')
    })

    element.addEventListener('mouseout', () => {
      isHovering = false
      setCursorImage(cursorElement, 'normal')
    })
  })
}

function setupCursor(cursorWrapper, cursorSrc) {
  const cursorAttributes = {
    src: cursorSrc,
    alt: 'Custom cursor'
  }

  const cursor = createHTMLElement('img', cursorAttributes)
  cursorWrapper.appendChild(cursor)
  cursorWrapper.style.display = 'block'
  cursorWrapper.style.opacity = 1
}

function initializeCustomCursor(DOMElement) {
  const cursorWrapper = document.querySelector(DOMElement)
  
  if ( isMobile() || !cursorWrapper ) return
  
  setupCursor(cursorWrapper, cursorSrc.darkNormal)

  const cursorImage = cursorWrapper.firstElementChild
  const hoverElements = returnDOMElements(linkElementSelectors)
  let mouseCoordinates = { x: 0, y: 0 }

  handleCursorChange(cursorImage, hoverElements)
  
  document.addEventListener('mousemove', (e) => {
    mouseCoordinates = getMousePosition(e)
  
    updateCursorPosition(cursorWrapper, mouseCoordinates)
    cursorShouldChange(cursorImage, mouseCoordinates, handleCursorChange, hoverElements)
  })
  
  window.addEventListener('scroll', () => {
    cursorShouldChange(cursorImage, mouseCoordinates, handleCursorChange, hoverElements)
  })
  
  document.addEventListener('mouseleave', () => {
    cursorWrapper.style.display = 'none'
    cursorWrapper.style.opacity = 0
  })
  
  document.addEventListener('mouseenter', () => {
    cursorWrapper.style.display = 'block'
    cursorWrapper.style.opacity = 1
  })
}

// Event listener that runs after the DOM is fully loaded
window.addEventListener('DOMContentLoaded', () => {
  // Array of CSS selectors identifying various forms for field animation
  const formSelectors = [
    '.contact-form',
    '#wpdMainContainer',
    '#create_customer',
    '#customer_login',
    '[action="/account/recover"]',
    '[action="/account/reset"]',
    '#address_form_new',
    '#address_form_9367216259347',
    '#activate-account'
  ];

  // Initialize form field animation for a specific form identified by '#customer_login' within the container 'wpd_main_container'
  wrapInputFieldsAndAddLabels('wpd_main_container', '#customer_login');

  // Loop through all selectors to initialize form label animations
  formSelectors.forEach(selector => {
    initFormLabelAnimation(selector);
  });

  initializeCustomCursor('.mx-custom-cursor')
});